import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useStreamAlerts } from '@/components/layout/stream-state-chip';
import { useFirstPartyAlerts } from '@/components/layout/first-party-chip';
import { usePulse } from '@/components/layout/data-pulse-chip';
import { useZoneAlerts } from '@/components/layout/zone-alert';
import { useNumberCheckAlerts } from '@/components/layout/number-check-alert';
import { useConnectionAlerts } from '@/components/layout/connection-alerts';
import { useCrmStageAlerts } from '@/components/layout/crm-stage-alerts';

/**
 * THE ALERTS BELL - every "look at this" the top bar used to carry as a pill.
 *
 * The owner, 28 Sep 2026: "too many pills on the menu bar ... show an alerts
 * tab where other pills can be added. It looks too much and overwhelming."
 * Sending off, the first-party verdict (old tag, not first-party, stopped),
 * ad spend behind and tracking lost each sat in the bar as their own coloured
 * pill, beside the workspace, the search and the date picker. They live here
 * now: one bell, a count of what needs someone, and one row each.
 *
 * ADDING AN ALERT IS ONE ENTRY in ALERT_SOURCES: a hook that returns an array
 * of rows (empty when there is nothing to say). A row is
 *   { id, tone: 'bad' | 'warn' | 'notice' | 'info', text, to?, linkLabel?, detail? }
 * `text` is one line. `to`/`linkLabel` is its existing link. `detail` is a
 * render function for the panel the old pill opened; the row opens it in place.
 * `info` rows are status, listed but not counted in the badge.
 *
 * The hooks are called in a fixed order on every render, so the array must
 * never be built conditionally.
 *
 * Not here: the full-width notices above the bar (BannerStack in app-shell).
 * Those carry their own inline controls (a currency to confirm and Save, a
 * default value to choose), and the impersonation notice must stay on screen
 * during support; they are already one-at-a-time.
 */

/** Ad spend behind, or tracking lost: the freshness dot's amber and rose. */
function usePulseAlerts() {
  const p = usePulse();
  if (!p) return [];
  const out = [];
  if (p.lost) {
    out.push({ id: 'tracking-lost', tone: 'bad', text: p.lost.title || 'Tracking lost on your live site',
      to: p.lost.action?.href || '/settings?tab=tracking&sub=install', linkLabel: p.lost.action?.label || 'Get the site script' });
  }
  if (p.spendLag) {
    const names = p.lateSpend.map((f) => f.label).filter(Boolean);
    out.push({ id: 'spend-behind', tone: 'warn',
      text: `Ad spend is ${p.spendLag} days behind${names.length ? ` (${names.join(', ')})` : ''}, so every return number it touches reads low`,
      to: '/integrations?cat=spend', linkLabel: 'Check the connection' });
  }
  return out;
}

// The registry. Order is the order rows are listed in, after sorting by tone.
const ALERT_SOURCES = [usePulseAlerts, useStreamAlerts, useFirstPartyAlerts, useZoneAlerts, useNumberCheckAlerts, useConnectionAlerts, useCrmStageAlerts];

const RANK = { bad: 0, warn: 1, notice: 2, info: 3 };
const DOT = { bad: 'bg-rose-500', warn: 'bg-amber-500', notice: 'bg-muted-foreground/60', info: 'bg-sky-500' };

export function useTopbarAlerts() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const rows = ALERT_SOURCES.flatMap((use) => use() || []);
  return rows.sort((a, b) => (RANK[a.tone] ?? 9) - (RANK[b.tone] ?? 9));
}

function AlertRow({ a, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-border/70 last:border-0">
      <div className="flex items-start gap-2.5 px-3.5 py-2.5">
        <span className={cn('mt-[7px] size-2 shrink-0 rounded-full', DOT[a.tone] || DOT.info)} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] leading-snug text-foreground">{a.text}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
            {a.to && (
              <Link to={a.to} onClick={onNavigate}
                className="rounded-sm font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {a.linkLabel || 'Open'}
              </Link>
            )}
            {a.detail && (
              <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
                className="inline-flex items-center gap-1 rounded-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {open ? 'Hide details' : 'Details'}
                <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
              </button>
            )}
          </div>
        </div>
      </div>
      {open && a.detail && <div className="border-t border-border/70 bg-surface-subtle px-3.5 py-3">{a.detail()}</div>}
    </li>
  );
}

export function AlertsBell() {
  const alerts = useTopbarAlerts();
  const [open, setOpen] = useState(false);
  const counted = alerts.filter((a) => a.tone !== 'info');
  const n = counted.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          aria-label={n ? `Alerts: ${n} need${n === 1 ? 's' : ''} you` : 'Alerts: nothing needs you'}
          title={n ? `${n} alert${n === 1 ? '' : 's'}` : 'Nothing needs you'}
          className={cn('relative grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
            open && 'bg-accent text-foreground')}>
          <Bell className="size-[18px]" />
          {/* Red whenever something needs a person: the owner read a grey
              badge as decoration (29 Sep 2026). Amber and grey said "worst
              open item" and nobody reads a badge that finely. */}
          {n > 0 && (
            <span className="absolute right-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[11px] font-semibold leading-none tabular-nums text-white ring-2 ring-sidebar">
              {n > 9 ? '9+' : n}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[min(80vh,640px)] w-[min(480px,calc(100vw-24px))] overflow-y-auto p-0">
        <div className="flex items-baseline justify-between border-b border-border px-3.5 py-2.5">
          <p className="text-[14px] font-semibold">Alerts</p>
          {n > 0 && <span className="text-[13px] tabular-nums text-muted-foreground">{n} need{n === 1 ? 's' : ''} you</span>}
        </div>
        {alerts.length === 0 ? (
          <p className="px-3.5 py-6 text-center text-[13.5px] text-muted-foreground">Nothing needs you</p>
        ) : (
          <ul>
            {alerts.map((a) => <AlertRow key={a.id} a={a} onNavigate={() => setOpen(false)} />)}
            {n === 0 && <li className="px-3.5 py-2.5 text-[13px] text-muted-foreground">Nothing needs you; the rows above are status.</li>}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default AlertsBell;
